import {
  AccordionGroup,
  AlertBanner,
  AlertVariants,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  HedgeControlStatusEnum,
  IndicatorBadge,
  IndicatorBadgeVariants,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  useObservableValue,
  usePersistedTabs,
  usePortal,
  useTabs,
} from '@talos/kyoko';
import { type ReactNode, useMemo } from 'react';
import { map } from 'rxjs';
import { useHedgePositionStatus } from '../../../providers/HedgePositionStatusProvider';
import { DEFAULT_HEDGE_POSITION_STATUS_TABS, HEDGE_POSITION_STATUS_TABS_EXCLUDED_IDS } from './defaults';
import { HedgePositionStatusBlotterTable } from './HedgePositionStatusBlotterTable';
import {
  BLOTTER_ID_ALL_STATUS,
  BLOTTER_ID_ERROR_STATUS,
  BLOTTER_ID_HEDGING_STATUS,
  BLOTTER_ID_WAITING_STATUS,
  PERSISTED_TABS_ID_PREFIX,
} from './tokens';
import type { HedgePositionStatusTabProps } from './types';

type DefaultTabItemID = (typeof HEDGE_POSITION_STATUS_TABS_EXCLUDED_IDS)[number];
type TabSuffixMap = Record<DefaultTabItemID, ReactNode>;

export function HedgePositionStatus() {
  const persistedTabs = usePersistedTabs<HedgePositionStatusTabProps>(PERSISTED_TABS_ID_PREFIX, {
    defaultInitialItems: DEFAULT_HEDGE_POSITION_STATUS_TABS,
    defaultInitialSelectedIndex: 0,
    excludedIDs: HEDGE_POSITION_STATUS_TABS_EXCLUDED_IDS,
  });

  const tabs = useTabs<HedgePositionStatusTabProps>({
    ...persistedTabs,
    showAddTab: true,
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  const { hedgePositionStatusObs, globalHedgeRule } = useHedgePositionStatus();

  const { errorCount, hedgingCount, waitingCount } = useObservableValue(
    () =>
      hedgePositionStatusObs.pipe(
        map(hedgePositionStatus => {
          return {
            errorCount:
              Array.from(hedgePositionStatus.values()).filter(
                h => h.HedgeControlStatus === HedgeControlStatusEnum.Error
              ).length ?? 0,
            hedgingCount:
              Array.from(hedgePositionStatus.values()).filter(
                h => h.HedgeControlStatus === HedgeControlStatusEnum.Hedging
              ).length ?? 0,
            waitingCount:
              Array.from(hedgePositionStatus.values()).filter(
                h => h.HedgeControlStatus === HedgeControlStatusEnum.Waiting
              ).length ?? 0,
          };
        })
      ),
    [hedgePositionStatusObs],
    { errorCount: 0, hedgingCount: 0, waitingCount: 0 }
  );

  const tabSuffixMap: TabSuffixMap = useMemo(() => {
    return {
      [BLOTTER_ID_ALL_STATUS]: null,
      [BLOTTER_ID_ERROR_STATUS]: (
        <IndicatorBadge ml="spacing4" children={errorCount} variant={IndicatorBadgeVariants.Negative} />
      ),
      [BLOTTER_ID_HEDGING_STATUS]: (
        <IndicatorBadge ml="spacing4" children={hedgingCount} variant={IndicatorBadgeVariants.Positive} />
      ),
      [BLOTTER_ID_WAITING_STATUS]: (
        <IndicatorBadge ml="spacing4" children={waitingCount} variant={IndicatorBadgeVariants.Information} />
      ),
    };
  }, [errorCount, hedgingCount, waitingCount]);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      {globalHedgeRule?.HedgeControlStatus === HedgeControlStatusEnum.Disabled && (
        <AlertBanner variant={AlertVariants.Negative}>Position Autohedging is currently disabled.</AlertBanner>
      )}
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} suffix={tabSuffixMap[tab.id as keyof typeof tabSuffixMap]} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map((tab, idx) => (
          <AccordionGroup key={tab.id}>
            <HedgePositionStatusBlotterTable key={idx} blotterID={`${PERSISTED_TABS_ID_PREFIX}/${tab.id}`} tab={tab} />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}
